import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIVE_CHILD_KEY = 'nestwise_active_child';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [activeChildName, setActiveChildName] = useState(localStorage.getItem(ACTIVE_CHILD_KEY) || null);
  // Global progress version — bump whenever a lesson completion is saved so
  // dashboards / planners / garden re-fetch without relying on a route remount.
  const [progressVersion, setProgressVersion] = useState(0);
  const bumpProgress = useCallback(() => setProgressVersion(v => v + 1), []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem(ACTIVE_CHILD_KEY);
    setToken(null);
    setUser(null);
    setActiveChildName(null);
  }, []);

  // Ensure activeChildName always points at a valid child once user is loaded.
  const reconcileActiveChild = useCallback((u) => {
    const kids = u?.children || [];
    if (kids.length === 0) {
      setActiveChildName(null);
      localStorage.removeItem(ACTIVE_CHILD_KEY);
      return;
    }
    const stored = localStorage.getItem(ACTIVE_CHILD_KEY);
    const valid = kids.find(c => c.name === stored);
    const chosen = valid ? valid.name : kids[0].name;
    setActiveChildName(chosen);
    localStorage.setItem(ACTIVE_CHILD_KEY, chosen);
  }, []);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { setUser(res.data); reconcileActiveChild(res.data); setLoading(false); })
        .catch(() => { logout(); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token, logout, reconcileActiveChild]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    reconcileActiveChild(res.data.user);
    return res.data;
  };

  const register = async (email, password, family_name, children, terms_accepted = false) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, family_name, children, terms_accepted });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    reconcileActiveChild(res.data.user);
    return res.data;
  };

  const setActiveChild = useCallback((name) => {
    setActiveChildName(name);
    if (name) localStorage.setItem(ACTIVE_CHILD_KEY, name);
    else localStorage.removeItem(ACTIVE_CHILD_KEY);
  }, []);

  const updateChildren = useCallback(async (newChildren) => {
    const res = await axios.put(`${API}/family/children`, { children: newChildren },
      { headers: { Authorization: `Bearer ${token}` } });
    const updatedUser = { ...user, children: res.data.children };
    setUser(updatedUser);
    reconcileActiveChild(updatedUser);
    return res.data.children;
  }, [token, user, reconcileActiveChild]);

  // Memoise so effect deps across the app don't re-fire every render (avoids
  // racy overlapping fetches that could clobber fresh data with stale responses).
  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );
  const childParam = activeChildName ? `?child=${encodeURIComponent(activeChildName)}` : '';
  const activeChild = (user?.children || []).find(c => c.name === activeChildName) || null;

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, authHeaders,
      activeChild, activeChildName, setActiveChild, childParam, updateChildren,
      progressVersion, bumpProgress,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
}
